import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { GetCompanySubscriptionAPI } from '../api/API';
import { PLAN_FEATURES, FEATURE_OPERATIONS } from '../consts/PlanFeatures';
import { MODULES, MODULE_FEATURES } from '../consts/ModuleIds';

// Criar o contexto
const SubscriptionContext = createContext();

/**
 * Provider do contexto de assinatura
 * Gerencia o estado das assinaturas e fornece métodos para verificar permissões
 */
export const SubscriptionProvider = ({ children }) => {
  // Estados
  const [subscription, setSubscription] = useState(null);
  const [addons, setAddons] = useState([]);
  const [planConfig, setPlanConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [trialInfo, setTrialInfo] = useState(null);
  const [hasAccess, setHasAccess] = useState(true); // fallback permissivo
  const [accessReason, setAccessReason] = useState(null);
  const [lastActivePlan, setLastActivePlan] = useState(null);
  const [minimumAllowedPlanOrder, setMinimumAllowedPlanOrder] = useState(null);
  // Encerramento da conta (LGPD): ciclo aberto (grace/expired) ou null
  const [closure, setClosure] = useState(null);
  const [closureCanRequest, setClosureCanRequest] = useState(false);
  
  // Cache de 1 hora (60 minutos * 60 segundos * 1000 milissegundos)
  const CACHE_DURATION = 60 * 60 * 1000;
  
  // Usar API diretamente para evitar dependência do DataProvider
  
  // Carregar dados da assinatura
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        setLoading(true);
        
        GetCompanySubscriptionAPI((result) => {
          if (result.success) {
            setSubscription(result.subscription);
            setAddons(result.addons || []);
            // plan_config: null se não houver config (frontend usa DEFAULT_PLAN_CONFIG)
            setPlanConfig(result.plan_config || null);
            // NOVO: capturar campos de trial
            setTrialInfo(result.trial_info || null);
            setHasAccess(result.has_access !== undefined ? result.has_access : true);
            setAccessReason(result.access_reason || null);
            // last_active_plan: último plano ativo (para bloquear downgrade)
            setLastActivePlan(result.last_active_plan || null);
            setMinimumAllowedPlanOrder(result.minimum_allowed_plan_order ?? null);
            setClosure(result.closure || null);
            setClosureCanRequest(result.closure_can_request === true);
            setError(null);
            setLastRefreshTime(new Date());
          } else {
            setError('Erro ao carregar os detalhes da assinatura');
          }
          setLoading(false);
        });
      } catch (err) {
        setError('Erro ao carregar os detalhes da assinatura');
        
        setLoading(false);
      }
    };
    
    fetchSubscription();
  }, []);
  
  /**
   * Verifica se a empresa tem uma assinatura de plano ativa
   * @returns {boolean} True se a empresa tem uma assinatura ativa, em período de teste ou cancelando
   */
  const hasActiveSubscription = useCallback(() => {
    // Verificar assinatura Stripe ativa (lógica atual)
    const hasStripeSubscription = subscription !== null && subscription && (
      subscription.has_active_plan ||
      subscription.subscription_status === 'trialing' ||
      subscription.stripe_subscription_status === 'trialing' ||
      subscription.subscription_status === 'canceling' ||
      subscription.stripe_subscription_status === 'canceling'
    );
    
    if (hasStripeSubscription) return true;
    
    // NOVO: Verificar trial interno ativo
    if (trialInfo && trialInfo.is_trial_active === true) return true;
    
    // NOVO: Usar has_access do backend como fallback em caso de erro
    if (hasAccess === true && accessReason === 'error_fallback') return true;

    // A chamada falhou (timeout, rede, erro do servidor) e não há dado nenhum de
    // assinatura: o status é DESCONHECIDO, não "sem plano". Manter o acesso liberado
    // até uma resposta válida — mandar um cliente pagante para /subscription/plans
    // por causa de uma falha temporária é o pior dos dois erros.
    if (error && subscription === null && trialInfo === null) return true;

    return false;
  }, [subscription, trialInfo, hasAccess, accessReason, error]);
  
  /**
   * Verifica se a empresa tem um módulo específico ativo
   * @param {string} moduleId - ID do módulo a ser verificado
   * @returns {boolean} True se a empresa tem o módulo ativo, em período de teste ou cancelando
   */
  const hasActiveModule = useCallback((moduleId) => {
    if (!addons || addons.length === 0) return false;
    
    return addons.some(
      addon => addon.module_id === moduleId &&
      (addon.subscription_status === 'active' ||
       addon.subscription_status === 'trialing' ||
       addon.subscription_status === 'canceling')
    );
  }, [addons]);
  
  /**
   * Verifica se a empresa pode acessar uma feature específica com base no plano
   * @param {string} featureKey - Chave da feature a ser verificada
   * @returns {boolean} True se a empresa pode acessar a feature
   */
  const canAccessFeature = useCallback((featureKey) => {
    if (!subscription) return false;
    
    // Verificar se a assinatura está ativa, em período de teste ou cancelando
    const isActive = subscription.has_active_plan ||
                    subscription.subscription_status === 'trialing' ||
                    subscription.stripe_subscription_status === 'trialing' ||
                    subscription.subscription_status === 'canceling' ||
                    subscription.stripe_subscription_status === 'canceling';
                    
    if (!isActive) return false;
    
    const planType = subscription.plan_type?.toLowerCase() || '';
    const features = PLAN_FEATURES[planType] || [];
    
    return features.includes(featureKey);
  }, [subscription]);
  
  /**
   * Verifica se a empresa pode realizar uma operação específica
   * @param {string} operation - Operação a ser verificada (ex: 'statistics.read')
   * @returns {boolean} True se a empresa pode realizar a operação
   */
  const canPerformOperation = useCallback((operation) => {
    // Primeiro verifica se a operação está disponível no plano atual
    if (!subscription) return false;
    
    // Verificar se a assinatura está ativa, em período de teste ou cancelando
    const isActive = subscription.has_active_plan ||
                    subscription.subscription_status === 'trialing' ||
                    subscription.stripe_subscription_status === 'trialing' ||
                    subscription.subscription_status === 'canceling' ||
                    subscription.stripe_subscription_status === 'canceling';
                    
    if (!isActive) return false;
    
    const planType = subscription.plan_type?.toLowerCase() || '';
    const planFeatures = PLAN_FEATURES[planType] || [];
    
    // Verifica se alguma feature do plano permite a operação
    for (const feature of planFeatures) {
      const operations = FEATURE_OPERATIONS[feature] || [];
      if (operations.includes(operation)) {
        return true;
      }
    }
    
    // Se não encontrou nas features do plano, verifica nos módulos adicionais
    if (!addons || addons.length === 0) return false;
    
    // Verifica se algum módulo ativo, em período de teste ou cancelando permite a operação
    for (const addon of addons) {
      if (addon.subscription_status !== 'active' &&
          addon.subscription_status !== 'trialing' &&
          addon.subscription_status !== 'canceling') continue;
      
      const moduleId = addon.module_id;
      const moduleOperations = MODULE_FEATURES[moduleId] || [];
      
      if (moduleOperations.includes(operation)) {
        return true;
      }
    }
    
    return false;
  }, [subscription, addons]);
  
  /**
   * Recarrega os dados da assinatura
   * @param {boolean} forceRefresh - Se true, força a atualização ignorando o cache
   */
  const refreshSubscription = useCallback((forceRefresh = false) => {
    
    // Verificar se o cache ainda é válido (menos de 1 hora)
    if (!forceRefresh && lastRefreshTime) {
      const timeSinceLastRefresh = new Date().getTime() - lastRefreshTime.getTime();
      if (timeSinceLastRefresh < CACHE_DURATION) {
        return;
      }
    }
    
    setLoading(true);
    
    // fresh: depois de uma mudança, não aproveita a resposta compartilhada recente
    GetCompanySubscriptionAPI((result) => {
      if (result.success) {
        setSubscription(result.subscription);
        setAddons(result.addons || []);
        // plan_config: null se não houver config (frontend usa DEFAULT_PLAN_CONFIG)
        setPlanConfig(result.plan_config || null);
        // NOVO: capturar campos de trial no refresh
        setTrialInfo(result.trial_info || null);
        setHasAccess(result.has_access !== undefined ? result.has_access : true);
        setAccessReason(result.access_reason || null);
        // last_active_plan: último plano ativo (para bloquear downgrade)
        setLastActivePlan(result.last_active_plan || null);
        setMinimumAllowedPlanOrder(result.minimum_allowed_plan_order ?? null);
        setClosure(result.closure || null);
        setClosureCanRequest(result.closure_can_request === true);
        setError(null);
        setLastRefreshTime(new Date());
        } else {
          setError('Erro ao atualizar os detalhes da assinatura');
      }
      setLoading(false);
    }, { fresh: forceRefresh });
  }, [lastRefreshTime, CACHE_DURATION]);
  
  /**
   * Invalida o cache forçando uma nova busca na próxima chamada
   * Útil quando sabemos que houve mudanças na assinatura (ex: nova assinatura, cancelamento, etc.)
   */
  const invalidateCache = useCallback(() => {
    setLastRefreshTime(null);
  }, []);

  /**
   * Verifica se a empresa está em trial interno ativo
   * @returns {boolean} True se estiver em trial ativo
   */
  const isInActiveTrial = useCallback(() => {
    return trialInfo !== null && trialInfo.is_trial_active === true;
  }, [trialInfo]);

  /**
   * Retorna o número de dias restantes no trial
   * @returns {number} Dias restantes no trial (0 se não houver trial)
   */
  const getTrialDaysRemaining = useCallback(() => {
    if (!trialInfo) return 0;
    return trialInfo.days_remaining || 0;
  }, [trialInfo]);
  
  // Valor do contexto
  const contextValue = {
    subscription,
    addons,
    planConfig,
    loading,
    error,
    lastRefreshTime,
    trialInfo,
    hasAccess,
    accessReason,
    lastActivePlan,
    minimumAllowedPlanOrder,
    closure,
    closureCanRequest,
    hasActiveSubscription,
    hasActiveModule,
    canAccessFeature,
    canPerformOperation,
    refreshSubscription,
    invalidateCache,
    isInActiveTrial,
    getTrialDaysRemaining,
  };
  
  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

/**
 * Hook personalizado para usar o contexto de assinatura
 * @returns {Object} Contexto de assinatura
 */
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  
  if (!context) {
    throw new Error('useSubscription deve ser usado dentro de um SubscriptionProvider');
  }
  
  return context;
};